import React, { useEffect, useRef, useState } from 'react';
import { decimalHoursToHm, hmToDecimalHours } from '../../utils/durationHours';

const fieldStyle = {
  flex: 1,
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '10px 12px',
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid rgba(201,168,76,0.3)',
  borderRadius: '8px',
  color: '#e8e0d0',
  fontSize: '13px',
  fontVariantNumeric: 'tabular-nums',
};

const labelStyle = {
  color: '#8896a8',
  fontSize: '10px',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function isEmptyParentValue(value) {
  return value === '' || value == null || value === undefined;
}

function draftsFromValue(value, allowEmpty) {
  if (allowEmpty && isEmptyParentValue(value)) {
    return { hours: '', minutes: '' };
  }
  const { hours, minutes } = decimalHoursToHm(Number(value) || 0);
  return { hours: String(hours), minutes: String(minutes) };
}

function digitsOnly(raw) {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Hours + Minutes picker. Parent keeps decimal hours (or '').
 * Draft strings edit freely; clamp/commit only on blur (leaving the picker) or Enter.
 * Re-sync from parent value only when nothing in this picker is focused
 * (avoids overwriting in-progress typing and silent re-open drift).
 */
export default function DurationHmPicker({
  value,
  onChange,
  minHours = 0,
  maxHours = 999,
  disabled = false,
  allowEmpty = false,
  idPrefix = 'duration-hm',
}) {
  const [hoursText, setHoursText] = useState(() => draftsFromValue(value, allowEmpty).hours);
  const [minutesText, setMinutesText] = useState(() => draftsFromValue(value, allowEmpty).minutes);
  const focusedFieldRef = useRef(null);
  const hoursTextRef = useRef(hoursText);
  const minutesTextRef = useRef(minutesText);
  const allowEmptyRef = useRef(allowEmpty);
  const minHoursRef = useRef(minHours);
  const maxHoursRef = useRef(maxHours);
  const onChangeRef = useRef(onChange);

  hoursTextRef.current = hoursText;
  minutesTextRef.current = minutesText;
  allowEmptyRef.current = allowEmpty;
  minHoursRef.current = minHours;
  maxHoursRef.current = maxHours;
  onChangeRef.current = onChange;

  // Re-sync drafts from parent only when value changes outside an in-progress edit.
  useEffect(() => {
    if (focusedFieldRef.current) return;
    const next = draftsFromValue(value, allowEmpty);
    setHoursText(next.hours);
    setMinutesText(next.minutes);
  }, [value, allowEmpty]);

  const clampDecimal = (decimal) => {
    let next = decimal;
    const min = Number(minHoursRef.current);
    const max = Number(maxHoursRef.current);
    if (Number.isFinite(min) && next < min) next = min;
    if (Number.isFinite(max) && next > max) next = max;
    return next;
  };

  const applyCommittedDrafts = (committedValue) => {
    const next = draftsFromValue(committedValue, allowEmptyRef.current);
    setHoursText(next.hours);
    setMinutesText(next.minutes);
  };

  const commit = () => {
    const hoursRaw = hoursTextRef.current;
    const minutesRaw = minutesTextRef.current;
    const allow = allowEmptyRef.current;
    const hoursBlank = hoursRaw === '';
    const minutesBlank = minutesRaw === '';

    if (allow && hoursBlank && minutesBlank) {
      onChangeRef.current?.('');
      applyCommittedDrafts('');
      return;
    }

    const h = hoursBlank ? 0 : Math.max(0, Math.floor(Number(hoursRaw) || 0));
    let m = minutesBlank ? 0 : Math.floor(Number(minutesRaw) || 0);
    if (!Number.isFinite(m) || m < 0) m = 0;
    if (m > 59) m = 59;

    const decimal = clampDecimal(hmToDecimalHours(h, m));
    if (allow && !(decimal > 0)) {
      onChangeRef.current?.('');
      applyCommittedDrafts('');
      return;
    }

    onChangeRef.current?.(decimal);
    applyCommittedDrafts(decimal);
  };

  const handleFocus = (field) => {
    focusedFieldRef.current = field;
  };

  const handleBlur = () => {
    focusedFieldRef.current = null;
    // Defer so tabbing Hours → Minutes does not commit mid-edit.
    requestAnimationFrame(() => {
      if (focusedFieldRef.current == null) commit();
    });
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commit();
    event.currentTarget.blur();
  };

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', width: '100%' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <label htmlFor={`${idPrefix}-hours`} style={labelStyle}>Hours</label>
        <input
          id={`${idPrefix}-hours`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          disabled={disabled}
          value={hoursText}
          onFocus={() => handleFocus('hours')}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(event) => setHoursText(digitsOnly(event.target.value))}
          placeholder="0"
          style={fieldStyle}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <label htmlFor={`${idPrefix}-minutes`} style={labelStyle}>Minutes</label>
        <input
          id={`${idPrefix}-minutes`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          disabled={disabled}
          value={minutesText}
          onFocus={() => handleFocus('minutes')}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(event) => setMinutesText(digitsOnly(event.target.value))}
          placeholder="0"
          style={fieldStyle}
        />
      </div>
    </div>
  );
}
